import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useFoundingHostCount } from "@/hooks/useFoundingHost"
import { Badge } from "@/components/ui/badge"
import { Users, Percent, Shield } from "lucide-react"

const LaunchHeroSection = () => {
  const { count, limit, isLoading } = useFoundingHostCount()
  const remainingSpots = limit - count
  
  return (
    <section 
      className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-b from-background via-background to-muted/30"
      role="banner"
      aria-labelledby="hero-title"
    >
      <div className="relative z-10 container mx-auto px-6 text-center max-w-4xl">
        
        {/* Badge */}
        <Badge variant="outline" className="mb-8 px-4 py-2 text-sm border-primary/30">
          <Users className="h-4 w-4 mr-2" />
          {isLoading ? '...' : `Noch ${remainingSpots} von ${limit} Gründerhost-Plätzen verfügbar`}
        </Badge>

        {/* Headline */}
        <h1 id="hero-title" className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
          Die Buchungsplattform
          <span className="block text-primary mt-2">
            von Vermietern für Vermieter.
          </span>
        </h1>

        {/* Core Message */}
        <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
          Fair-Stays ist der Gegenentwurf zu den anonymen Großportalen. Wir bieten eine professionelle 
          Buchungs-Infrastruktur, die nicht auf Profitmaximierung, sondern auf fairer Partnerschaft basiert.
        </p>

        <p className="text-base text-muted-foreground mb-12 max-w-2xl mx-auto">
          Warum wir das tun? Weil wir die Gebührenwillkür und Machtlosigkeit im eigenen Familienkreis erlebt haben.
        </p>

        {/* Value Props */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
          <div className="flex items-start gap-4 text-left p-4 rounded-lg bg-card border">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Percent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Dauerhaft fair</h3>
              <p className="text-sm text-muted-foreground">
                Nur 5% Provision statt der üblichen 10%+.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 text-left p-4 rounded-lg bg-card border">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Echte Mitbestimmung</h3>
              <p className="text-sm text-muted-foreground">
                Wir entwickeln eine Genossenschaft mit Veto-Recht bei allen wichtigen Entscheidungen.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mb-8">
          <p className="text-lg font-medium mb-4">
            Werde einer der ersten {limit} Gründungshosts und sichere dir lebenslange Sonderkonditionen.
          </p>
          <Link to="/gruenderhost">
            <Button 
              size="lg" 
              variant="hero" 
              className="text-xl px-12 py-6 rounded-full shadow-strong hover:shadow-medium transition-all"
            >
              Jetzt Pionier werden
            </Button>
          </Link>
        </div>

        {/* Counter */}
        {!isLoading && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="flex -space-x-2">
              {[...Array(Math.min(count, 5))].map((_, i) => (
                <div 
                  key={i} 
                  className="w-8 h-8 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center text-xs font-medium"
                >
                  {i + 1}
                </div>
              ))}
            </div>
            <span className="text-sm">
              {count > 0 
                ? `${count} ${count === 1 ? 'Pionier hat' : 'Pioniere haben'} sich bereits angemeldet`
                : 'Sei der erste Pionier!'
              }
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

export default LaunchHeroSection
